"use client";

import { useCallback, useRef, useState } from "react";
import Link from "next/link";
import { ChevronRight } from "lucide-react";
import { NieuwsFoto } from "@/components/nieuws-foto";

export type CarrouselItem = {
  id: number;
  titel: string;
  /** Al opgemaakt op de server, zodat hier geen Date overheen hoeft. */
  datumTekst: string;
  intro: string | null;
  foto: string | null;
  categorie: string | null;
};

// Nieuws als een rij die je opzij veegt.
//
// Het vegen doet de browser zelf: een horizontale scroll met snap-points. Dat
// is vloeiender dan wat wij met touch-events zouden bouwen, werkt met een muis
// en een trackpad net zo goed, en blijft met het toetsenbord bereikbaar omdat
// het gewone links blijven.
//
// De kaarten zijn iets smaller dan het scherm, zodat de volgende er half naast
// piept. Zonder dat randje ziet niemand dat er meer is.
export function NieuwsCarrousel({ items }: { items: CarrouselItem[] }) {
  const baan = useRef<HTMLUListElement>(null);
  const [actief, setActief] = useState(0);

  // Welke kaart staat er? Die waarvan de linkerrand het dichtst bij de
  // scrollpositie ligt. Dat werkt ongeacht kaartbreedte en tussenruimte.
  const opScroll = useCallback(() => {
    const el = baan.current;
    if (!el) return;
    const kinderen = Array.from(el.children) as HTMLElement[];
    let dichtst = 0;
    let kleinste = Infinity;
    kinderen.forEach((kind, i) => {
      const afstand = Math.abs(kind.offsetLeft - el.scrollLeft);
      if (afstand < kleinste) {
        kleinste = afstand;
        dichtst = i;
      }
    });
    setActief(dichtst);
  }, []);

  const gaNaar = (i: number) => {
    const el = baan.current;
    const kind = el?.children[i] as HTMLElement | undefined;
    if (!el || !kind) return;
    el.scrollTo({ left: kind.offsetLeft, behavior: "smooth" });
  };

  if (items.length === 0) {
    return (
      <p className="px-4 py-6 text-center text-sm text-muted-foreground">Nog geen nieuws beschikbaar.</p>
    );
  }

  return (
    <div>
      <ul
        ref={baan}
        onScroll={opScroll}
        className="flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-1"
      >
        {items.map((n) => (
          <li key={n.id} className="w-[85%] shrink-0 snap-start last:mr-4">
            <Link
              href={`/nieuws/${n.id}`}
              className="block h-full overflow-hidden rounded-xl border border-[#2a4a6f] bg-card transition-colors active:bg-[#24456b]"
            >
              <div className="relative aspect-video w-full overflow-hidden">
                <NieuwsFoto src={n.foto} alt={n.titel} />
                {n.categorie && (
                  <span className="absolute left-3 top-3 rounded bg-primary px-1.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-wide text-black">
                    {n.categorie}
                  </span>
                )}
              </div>
              <div className="p-4">
                <span className="text-xs capitalize text-muted-foreground">{n.datumTekst}</span>
                <h3 className="mt-0.5 line-clamp-2 font-display text-base font-bold leading-snug text-white">
                  {n.titel}
                </h3>
                {n.intro && <p className="mt-1 line-clamp-2 text-sm text-soft-foreground">{n.intro}</p>}
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-primary">
                  Lees meer
                  <ChevronRight className="size-3.5" aria-hidden />
                </span>
              </div>
            </Link>
          </li>
        ))}
      </ul>

      {items.length > 1 && (
        <div className="mt-3 flex items-center justify-center gap-2">
          {items.map((n, i) => (
            <button
              key={n.id}
              type="button"
              onClick={() => gaNaar(i)}
              aria-label={`Bericht ${i + 1} van ${items.length}`}
              aria-current={i === actief ? "true" : undefined}
              className={
                i === actief
                  ? "h-2 w-5 rounded-full bg-primary transition-all"
                  : "size-2 rounded-full bg-white/25 transition-all hover:bg-white/40"
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
